/**
 * JSX ports of the door symbol glyphs (ui/editor/editorSegmentSvg.ts), drawn
 * by the kind of the door's swing: hinged leaves, pocket, bifold, bypass,
 * sliding glass, and garage. Pure presentational: `wfn` maps (along, offset)
 * on the host line to world space, `sp` projects world → screen. Offsets are
 * measured OUTWARD from the inner wall line, and the wall occupies
 * [0, WALL_THICKNESS]: each door type sits across that thickness exactly as
 * its 3D counterpart does: in-swing on the inner face, out-swing on the
 * outer face, bifold / pocket / bypass / sliding glass centered, garage
 * flush with the inner face.
 */

import { type DoorKind, type DoorSwingShape, doorSwingShape, type FloorPlanEdgeSegment, type Point, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';
import { svgPoints } from '@kaiizen/kai-floor-plan-editor-core';
import { Fragment } from 'react';
import { kaiBlue, midtone, roomStroke } from '../../styles';

export type Wfn = (s: number, o: number) => Point;
export type Sp = (p: Point) => Point;

/** What each kind's symbol draws from: the host line's mappings, the door segment, and its swing's shape. */
interface KindSymbolProps {
  wfn: Wfn;
  sp: Sp;
  seg: FloorPlanEdgeSegment;
  shape: DoorSwingShape;
}

function InkPolyline({ points, width, dash }: { points: Point[]; width: number; dash?: string }): React.JSX.Element {
  return <polyline points={svgPoints(points)} fill='none' stroke={roomStroke} strokeWidth={width} strokeDasharray={dash || undefined} />;
}

/** A hinged door's leaves, each hung on the face it opens toward (the inner line for in-swing, the outer line for out-swing), with its swing arc. */
function HingedLeaves({ wfn, sp, seg: { position, width }, shape }: KindSymbolProps): React.JSX.Element {
  return (
    <>
      {shape.leaves.map(({ alongSign, intoSign, widthShare }, index) => {
        const hinge = alongSign > 0 ? position : position + width, length = width * widthShare;
        const face = intoSign > 0 ? 0 : WALL_THICKNESS;
        const startAngle = alongSign > 0 ? 0 : Math.PI, sweep = -alongSign * intoSign * (Math.PI / 2);
        const arc = Array.from({ length: 9 }, (_, step) => {
          const angle = startAngle + (sweep * step) / 8;
          return sp(wfn(hinge + length * Math.cos(angle), face + length * Math.sin(angle)));
        });
        const hingePoint = sp(wfn(hinge, face)), tip = sp(wfn(hinge, face - intoSign * length));
        return (
          <Fragment key={index}>
            <InkPolyline points={arc} width={1.5} />
            <line x1={hingePoint.x} y1={hingePoint.y} x2={tip.x} y2={tip.y} stroke={roomStroke} strokeWidth={2.5} />
          </Fragment>
        );
      })}
    </>
  );
}

/** A pocket door's visible half-panel, centered in the wall cavity over the half of the opening on its hand's side. */
function Pocket({ wfn, sp, seg: { position, width }, shape: { hand } }: KindSymbolProps): React.JSX.Element {
  const left = hand === 'left';
  const from = sp(wfn(left ? position : position + width / 2, WALL_THICKNESS / 2));
  const to = sp(wfn(left ? position + width / 2 : position + width, WALL_THICKNESS / 2));
  return <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={midtone} strokeWidth={4} />;
}

/**
 * 4-panel bifold: the standard plan symbol, each half a shallow V of two
 * hinged panels folding into the room (negative offsets are the interior),
 * forming a W.
 */
function Bifold({ wfn, sp, seg: { position, width } }: KindSymbolProps): React.JSX.Element {
  const middle = WALL_THICKNESS / 2, fold = middle - Math.min(width / 5, 1.2);
  const points = [[position, middle], [position + width / 4, fold], [position + width / 2, middle], [position + (3 * width) / 4, fold], [
    position + width,
    middle,
  ]]
    .map(([along, offset]) => sp(wfn(along, offset)));
  return <InkPolyline points={points} width={2} />;
}

/**
 * Bypass sliders: two overlapping panels on parallel tracks, drawn as offset
 * bars either side of the wall centerline, overlapping at the middle.
 */
function Bypass({ wfn, sp, seg: { position, width } }: KindSymbolProps): React.JSX.Element {
  const middle = WALL_THICKNESS / 2, track = 0.09, lap = Math.min(0.5, width * 0.12);
  const rear = [sp(wfn(position, middle + track)), sp(wfn(position + width / 2 + lap, middle + track))];
  const front = [sp(wfn(position + width / 2 - lap, middle - track)), sp(wfn(position + width, middle - track))];
  return (
    <>
      <InkPolyline points={rear} width={3.5} />
      <InkPolyline points={front} width={3.5} />
    </>
  );
}

/**
 * Sliding glass door: two overlapping glazed panels on parallel tracks,
 * bypass geometry with window-style glass bands. The tracks are the 3D's:
 * the fixed panel toward the outer face, drawn first, then the sliding panel
 * toward the room, on the door's hand.
 */
function SlidingGlass({ wfn, sp, seg: { position, width }, shape: { hand } }: KindSymbolProps): React.JSX.Element {
  const lap = Math.min(0.5, width * 0.12), halfBand = 0.07, left = hand === 'left';
  const pane = (paneStart: number, paneEnd: number, offset: number): React.JSX.Element => {
    const band = [wfn(paneStart, offset - halfBand), wfn(paneStart, offset + halfBand), wfn(paneEnd, offset + halfBand), wfn(paneEnd, offset - halfBand)];
    return (
      <>
        <polygon points={svgPoints(band.map(sp))} fill={kaiBlue} />
        <InkPolyline points={[sp(wfn(paneStart, offset)), sp(wfn(paneEnd, offset))]} width={1} />
      </>
    );
  };
  const slider: [number, number] = left ? [position, position + width / 2 + lap] : [position + width / 2 - lap, position + width];
  const fixed: [number, number] = left ? [position + width / 2 - lap, position + width] : [position, position + width / 2 + lap];
  return (
    <>
      {pane(fixed[0], fixed[1], WALL_THICKNESS * 0.65)}
      {pane(slider[0], slider[1], WALL_THICKNESS * 0.35)}
    </>
  );
}

/**
 * Overhead garage door: the closed panel just inside the wall, flush with
 * the room-side face, plus the dashed footprint of the raised door and its
 * tracks projecting into the room, as deep as the door is tall.
 */
function Garage({ wfn, sp, seg: { position, width, openingHeight } }: KindSymbolProps): React.JSX.Element {
  const inset = 0.15, depth = -(openingHeight ?? 7);
  const panel = [sp(wfn(position, 0.075)), sp(wfn(position + width, 0.075))];
  const left = [sp(wfn(position + inset, -0.3)), sp(wfn(position + inset, depth))];
  const right = [sp(wfn(position + width - inset, -0.3)), sp(wfn(position + width - inset, depth))];
  const leading = [sp(wfn(position + inset, depth)), sp(wfn(position + width - inset, depth))];
  return (
    <>
      <InkPolyline points={panel} width={4} />
      <InkPolyline points={left} width={1.5} dash='6 4' />
      <InkPolyline points={right} width={1.5} dash='6 4' />
      <InkPolyline points={leading} width={1.5} dash='6 4' />
    </>
  );
}

const KIND_SYMBOLS: Record<DoorKind, (props: KindSymbolProps) => React.JSX.Element> = {
  hinged: HingedLeaves,
  pocket: Pocket,
  bifold: Bifold,
  bypass: Bypass,
  garage: Garage,
  slidingGlass: SlidingGlass,
};

/** A door's plan symbol, drawn by the kind of its swing; a door of a swing this version doesn't know draws a hinged door with no leaves. */
export function DoorGlyph({ wfn, seg, sp }: { wfn: Wfn; seg: FloorPlanEdgeSegment; sp: Sp }): React.JSX.Element {
  const shape = doorSwingShape(seg.swing);
  const KindSymbol = KIND_SYMBOLS[shape.kind];
  return <KindSymbol wfn={wfn} sp={sp} seg={seg} shape={shape} />;
}
